"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkReqCoverage = checkReqCoverage;
exports.isReqSatisfied = isReqSatisfied;
/**
 * Pure deterministic coverage check. No IO, no AI.
 *
 * Rules:
 * - every **active** `Included` id must be cited by ≥1 step (else it's uncovered),
 * - no step may cite an `Excluded` id (a violation),
 * - a citation must resolve to an Included or Constraint id (else it's unknown).
 *
 * Constraints are boundaries, not deliverables — a step *may* cite one but is
 * never *required* to, so constraints never appear in `uncovered`.
 *
 * A `dropped` Included item is retired: it stays a valid citation target (so old
 * `satisfies` references never become `unknownCitations`) but is exempt from the
 * coverage requirement, so it never appears in `uncovered`.
 */
function checkReqCoverage(req, steps) {
    const includedIds = new Set(req.included.map(i => i.id));
    const constraintIds = new Set(req.constraints.map(c => c.id));
    const excludedIds = new Set(req.excluded.map(e => e.id));
    const citedIds = new Set();
    const excludedViolations = [];
    const unknownCitations = [];
    for (const step of steps) {
        for (const id of step.satisfies ?? []) {
            citedIds.add(id);
            if (excludedIds.has(id)) {
                excludedViolations.push({ stepOrder: step.order, id });
            }
            else if (!includedIds.has(id) && !constraintIds.has(id)) {
                unknownCitations.push({ stepOrder: step.order, id });
            }
        }
    }
    const uncovered = req.included.filter(i => i.status !== 'dropped' && !citedIds.has(i.id));
    return { uncovered, excludedViolations, unknownCitations };
}
/** True when the plan fully and faithfully honours the req (nothing dropped, invented, or dangling). */
function isReqSatisfied(coverage) {
    return (coverage.uncovered.length === 0 &&
        coverage.excludedViolations.length === 0 &&
        coverage.unknownCitations.length === 0);
}
